import React from "react";
import {
  Alert,
  Button,
  Col,
  FormText,
  Input,
  Label as RsLabel,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import {
  CatalogoChofer,
  CatalogoCliente,
  CatalogoVehiculo,
  ClienteFormState,
  ChoferFormState,
  VehiculoFormState,
} from "./MovimientoBase.types";

export type ClienteSeleccionModalProps = {
  abierto: boolean;
  busqueda: string;
  cargando: boolean;
  error: string | null;
  resultados: CatalogoCliente[];
  onClose: () => void;
  onBusquedaChange: (value: string) => void;
  onBuscar: () => void;
  onCrear: () => void;
  onSeleccion: (id: string) => void;
};

export const ClienteSeleccionModal: React.FC<ClienteSeleccionModalProps> = ({
  abierto,
  busqueda,
  cargando,
  error,
  resultados,
  onClose,
  onBusquedaChange,
  onBuscar,
  onCrear,
  onSeleccion,
}) => (
  <Modal isOpen={abierto} toggle={onClose} size="lg">
    <ModalHeader toggle={onClose}>Seleccionar cliente</ModalHeader>
    <ModalBody>
      <div className="d-flex gap-2 mb-3">
        <Input
          placeholder="Buscar por nombre o documento..."
          value={busqueda}
          onChange={(e) => onBusquedaChange(e.target.value)}
        />
        <Button color="primary" type="button" onClick={onBuscar} disabled={cargando} className="text-nowrap">
          {cargando ? "Buscando..." : "Buscar"}
        </Button>
        <Button color="soft-success" type="button" className="text-nowrap" onClick={onCrear}>
          Crear cliente
        </Button>
      </div>
      {error && <Alert color="danger">{error}</Alert>}
      <div className="table-responsive">
        <Table hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Documento</th>
              <th style={{ width: "120px" }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {resultados.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center text-muted py-4">
                  {cargando ? "Buscando clientes..." : "Sin resultados"}
                </td>
              </tr>
            ) : (
              resultados.map((cliente) => (
                <tr key={cliente.id}>
                  <td>{cliente.razon_social}</td>
                  <td>{cliente.documento || "Sin dato"}</td>
                  <td>
                    <Button color="success" size="sm" onClick={() => onSeleccion(String(cliente.id))}>
                      Seleccionar
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </ModalBody>
  </Modal>
);

export type ChoferSeleccionModalProps = {
  abierto: boolean;
  busqueda: string;
  cargando: boolean;
  error: string | null;
  resultados: CatalogoChofer[];
  onClose: () => void;
  onBusquedaChange: (value: string) => void;
  onBuscar: () => void;
  onCrear: () => void;
  onSeleccion: (id: string) => void;
};

export const ChoferSeleccionModal: React.FC<ChoferSeleccionModalProps> = ({
  abierto,
  busqueda,
  cargando,
  error,
  resultados,
  onClose,
  onBusquedaChange,
  onBuscar,
  onCrear,
  onSeleccion,
}) => (
  <Modal isOpen={abierto} toggle={onClose} size="lg">
    <ModalHeader toggle={onClose}>Seleccionar chofer</ModalHeader>
    <ModalBody>
      <div className="d-flex gap-2 mb-3">
        <Input
          placeholder="Buscar por nombre o documento..."
          value={busqueda}
          onChange={(e) => onBusquedaChange(e.target.value)}
        />
        <Button color="primary" type="button" onClick={onBuscar} disabled={cargando} className="text-nowrap">
          {cargando ? "Buscando..." : "Buscar"}
        </Button>
        <Button color="soft-success" type="button" className="text-nowrap" onClick={onCrear}>
          Crear chofer
        </Button>
      </div>
      {error && <Alert color="danger">{error}</Alert>}
      <div className="table-responsive">
        <Table hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Chofer</th>
              <th>Documento</th>
              <th>Vehículo asignado</th>
              <th style={{ width: "120px" }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {resultados.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  {cargando ? "Buscando choferes..." : "Sin resultados"}
                </td>
              </tr>
            ) : (
              resultados.map((chofer) => (
                <tr key={chofer.id}>
                  <td>{chofer.nombre}</td>
                  <td>{chofer.documento || "Sin dato"}</td>
                  <td>
                    {chofer.vehiculoDescripcion
                      ? `${chofer.vehiculoDescripcion}${
                          chofer.vehiculoMatricula ? ` · ${chofer.vehiculoMatricula}` : ""
                        }`
                      : "Sin asignar"}
                  </td>
                  <td>
                    <Button color="success" size="sm" onClick={() => onSeleccion(String(chofer.id))}>
                      Seleccionar
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </ModalBody>
  </Modal>
);

export type VehiculoSeleccionModalProps = {
  abierto: boolean;
  busqueda: string;
  cargando: boolean;
  error: string | null;
  resultados: CatalogoVehiculo[];
  onClose: () => void;
  onBusquedaChange: (value: string) => void;
  onBuscar: () => void;
  onCrear: () => void;
  onSeleccion: (id: string) => void;
};

export const VehiculoSeleccionModal: React.FC<VehiculoSeleccionModalProps> = ({
  abierto,
  busqueda,
  cargando,
  error,
  resultados,
  onClose,
  onBusquedaChange,
  onBuscar,
  onCrear,
  onSeleccion,
}) => (
  <Modal isOpen={abierto} toggle={onClose} size="lg">
    <ModalHeader toggle={onClose}>Seleccionar vehículo</ModalHeader>
    <ModalBody>
      <div className="d-flex gap-2 mb-3">
        <Input
          placeholder="Buscar por descripción o matrícula..."
          value={busqueda}
          onChange={(e) => onBusquedaChange(e.target.value)}
        />
        <Button color="primary" type="button" onClick={onBuscar} disabled={cargando} className="text-nowrap">
          {cargando ? "Buscando..." : "Buscar"}
        </Button>
        <Button color="soft-success" type="button" className="text-nowrap" onClick={onCrear}>
          Crear vehículo
        </Button>
      </div>
      {error && <Alert color="danger">{error}</Alert>}
      <div className="table-responsive">
        <Table hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Vehículo</th>
              <th>Matrícula</th>
              <th style={{ width: "120px" }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {resultados.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center text-muted py-4">
                  {cargando ? "Buscando vehículos..." : "Sin resultados"}
                </td>
              </tr>
            ) : (
              resultados.map((vehiculo) => (
                <tr key={vehiculo.id}>
                  <td>{vehiculo.descripcion}</td>
                  <td>{vehiculo.matricula || "Sin dato"}</td>
                  <td>
                    <Button color="success" size="sm" onClick={() => onSeleccion(String(vehiculo.id))}>
                      Seleccionar
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </ModalBody>
  </Modal>
);

export type ClienteCrearModalProps = {
  abierto: boolean;
  error: string | null;
  form: ClienteFormState;
  creando: boolean;
  onClose: () => void;
  onChange: (field: keyof ClienteFormState, value: string) => void;
  onSubmit: () => void;
};

export const ClienteCrearModal: React.FC<ClienteCrearModalProps> = ({
  abierto,
  error,
  form,
  creando,
  onClose,
  onChange,
  onSubmit,
}) => (
  <Modal isOpen={abierto} toggle={onClose}>
    <ModalHeader toggle={onClose}>Crear cliente</ModalHeader>
    <ModalBody>
      {error && <Alert color="danger">{error}</Alert>}
      <Row className="g-3">
        <Col sm={12}>
          <RsLabel className="form-label fw-semibold">Nombre / Razón social</RsLabel>
          <Input value={form.razonSocial} onChange={(e) => onChange("razonSocial", e.target.value)} />
        </Col>
        <Col sm={12}>
          <RsLabel className="form-label fw-semibold">Documento</RsLabel>
          <Input value={form.documento} onChange={(e) => onChange("documento", e.target.value)} />
        </Col>
        <Col sm={12}>
          <RsLabel className="form-label fw-semibold">Dirección</RsLabel>
          <Input value={form.direccion} onChange={(e) => onChange("direccion", e.target.value)} />
        </Col>
        <Col sm={6}>
          <RsLabel className="form-label fw-semibold">Teléfono</RsLabel>
          <Input value={form.telefono} onChange={(e) => onChange("telefono", e.target.value)} />
        </Col>
        <Col sm={6}>
          <RsLabel className="form-label fw-semibold">Correo</RsLabel>
          <Input value={form.email} onChange={(e) => onChange("email", e.target.value)} />
        </Col>
      </Row>
    </ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={onClose}>
        Cancelar
      </Button>
      <Button color="primary" onClick={onSubmit} disabled={creando}>
        {creando ? "Guardando..." : "Guardar cliente"}
      </Button>
    </ModalFooter>
  </Modal>
);

export type ChoferCrearModalProps = {
  abierto: boolean;
  error: string | null;
  form: ChoferFormState;
  creando: boolean;
  onClose: () => void;
  onChange: (field: keyof ChoferFormState, value: string) => void;
  onSubmit: () => void;
};

export const ChoferCrearModal: React.FC<ChoferCrearModalProps> = ({
  abierto,
  error,
  form,
  creando,
  onClose,
  onChange,
  onSubmit,
}) => (
  <Modal isOpen={abierto} toggle={onClose}>
    <ModalHeader toggle={onClose}>Crear chofer</ModalHeader>
    <ModalBody>
      {error && <Alert color="danger">{error}</Alert>}
      <Row className="g-3">
        <Col sm={12}>
          <RsLabel className="form-label fw-semibold">Nombre completo</RsLabel>
          <Input value={form.nombre} onChange={(e) => onChange("nombre", e.target.value)} />
        </Col>
        <Col sm={12}>
          <RsLabel className="form-label fw-semibold">Documento / Licencia</RsLabel>
          <Input value={form.documento} onChange={(e) => onChange("documento", e.target.value)} />
        </Col>
        <Col sm={6}>
          <RsLabel className="form-label fw-semibold">Teléfono</RsLabel>
          <Input value={form.telefono} onChange={(e) => onChange("telefono", e.target.value)} />
        </Col>
        <Col sm={6}>
          <RsLabel className="form-label fw-semibold">Correo</RsLabel>
          <Input value={form.email} onChange={(e) => onChange("email", e.target.value)} />
        </Col>
      </Row>
    </ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={onClose}>
        Cancelar
      </Button>
      <Button color="primary" onClick={onSubmit} disabled={creando}>
        {creando ? "Guardando..." : "Guardar chofer"}
      </Button>
    </ModalFooter>
  </Modal>
);

export type VehiculoCrearModalProps = {
  abierto: boolean;
  error: string | null;
  form: VehiculoFormState;
  creando: boolean;
  onClose: () => void;
  onChange: (field: keyof VehiculoFormState, value: string) => void;
  onSubmit: () => void;
};

export const VehiculoCrearModal: React.FC<VehiculoCrearModalProps> = ({
  abierto,
  error,
  form,
  creando,
  onClose,
  onChange,
  onSubmit,
}) => (
  <Modal isOpen={abierto} toggle={onClose}>
    <ModalHeader toggle={onClose}>Crear vehículo</ModalHeader>
    <ModalBody>
      {error && <Alert color="danger">{error}</Alert>}
      <Row className="g-3">
        <Col sm={12}>
          <RsLabel className="form-label fw-semibold">Descripción</RsLabel>
          <Input value={form.descripcion} onChange={(e) => onChange("descripcion", e.target.value)} />
        </Col>
        <Col sm={12}>
          <RsLabel className="form-label fw-semibold">Matrícula / Chapa</RsLabel>
          <Input value={form.matricula} onChange={(e) => onChange("matricula", e.target.value)} />
        </Col>
        <Col sm={6}>
          <RsLabel className="form-label fw-semibold">Marca</RsLabel>
          <Input value={form.marca} onChange={(e) => onChange("marca", e.target.value)} />
        </Col>
        <Col sm={6}>
          <RsLabel className="form-label fw-semibold">Modelo</RsLabel>
          <Input value={form.modelo} onChange={(e) => onChange("modelo", e.target.value)} />
        </Col>
        <Col sm={6}>
          <RsLabel className="form-label fw-semibold">Color</RsLabel>
          <Input value={form.color} onChange={(e) => onChange("color", e.target.value)} />
        </Col>
      </Row>
    </ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={onClose}>
        Cancelar
      </Button>
      <Button color="primary" onClick={onSubmit} disabled={creando}>
        {creando ? "Guardando..." : "Guardar vehículo"}
      </Button>
    </ModalFooter>
  </Modal>
);
